<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'OnlinePreview',
})
</script>
<template>
	<el-dialog
		v-model="visible"
		:title="props.title || defaultTitle"
		:close-on-click-modal="false"
		:width="props.width"
		:top="props.top"
		@closed="handleClose"
	>
		<div :style="{ height: props.height, width: '100%', position: 'relative' }">
			<!-- 加载动画 -->
			<div v-show="loading" class="loading-overlay">
				<div class="loading-spinner">
					<div class="spinner-ring"></div>
					<div class="loading-text">{{ t('componentsModule.common.tableCom.loading') }}</div>
				</div>
			</div>
			<!-- iframe预览 -->
			<iframe v-show="!loading" :style="{ height: '100%', width: '100%' }" :src="previewUrl" frameborder="0" @load="handleIframeLoad"></iframe>
		</div>
	</el-dialog>
</template>

<script setup lang="ts" name="OnlinePreview">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getFileUrl } from '@/utils/tool'

const { t } = useI18n()

interface Props {
	// 控制弹窗显示隐藏
	modelValue: boolean
	// 文件URL或路径
	fileUrl?: string
	// 弹窗标题
	title?: string
	// 弹窗宽度
	width?: string
	// 弹窗距离顶部距离
	top?: string
	// 预览区域高度
	height?: string
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
	fileUrl: '',
	title: '',
	width: '86vw',
	top: '6vh',
	height: '80vh'
})

const emit = defineEmits<{
	'update:modelValue': [value: boolean]
	closed: []
}>()

const visible = ref(false)
const previewUrl = ref('')
const loading = ref(true)

// 计算属性：默认标题
const defaultTitle = computed(() => {
	if (!props.title) {
		return t('componentsModule.common.onlinePreview.preview')
	}
	return ''
})

// 监听modelValue变化
watch(
	() => props.modelValue,
	newVal => {
		visible.value = newVal
		if (newVal && props.fileUrl) {
			loading.value = true
			// 延迟设置URL，避免iframe加载问题
			setTimeout(() => {
				previewUrl.value = getFileUrl(props.fileUrl)
			}, 100)
		}
	},
	{ immediate: true }
)

// 监听visible变化，同步到父组件
watch(visible, newVal => {
	emit('update:modelValue', newVal)
})

// 关闭弹窗处理
const handleClose = () => {
	visible.value = false
	previewUrl.value = ''
	loading.value = false
	emit('closed')
}

// iframe加载完成处理
const handleIframeLoad = () => {
	if (previewUrl.value) {
		loading.value = false
	}
}

// 打开预览
const openPreview = (url: string) => {
	previewUrl.value = ''
	loading.value = true
	visible.value = true

	setTimeout(() => {
		previewUrl.value = getFileUrl(url)
	}, 100)
}

// 暴露方法给父组件使用
defineExpose({
	openPreview,
	handleClose
})
</script>

<style lang="scss" scoped>
.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.loading-spinner {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
}

.spinner-ring {
	width: 60px;
	height: 60px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid #409eff;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	position: relative;
}

.spinner-ring::before {
	content: '';
	position: absolute;
	top: -4px;
	left: -4px;
	right: -4px;
	bottom: -4px;
	border: 2px solid transparent;
	border-top: 2px solid #67c23a;
	border-radius: 50%;
	animation: spin 2s linear infinite reverse;
}

.spinner-ring::after {
	content: '';
	position: absolute;
	top: 8px;
	left: 8px;
	right: 8px;
	bottom: 8px;
	border: 2px solid transparent;
	border-top: 2px solid #e6a23c;
	border-radius: 50%;
	animation: spin 1.5s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.loading-text {
	font-size: 16px;
	color: #606266;
	font-weight: 500;
	animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.6;
	}
}
</style>
